import { api } from '../client';

import type { ListResponse, PaginationParams, StockAuditEvent } from './common';
import {
  rememberResponseItemsUpdatedAt,
  rememberResponseUpdatedAt,
  type StockDocumentMutationDeps,
  type StockDocumentMutationOptions,
} from './documentMutationCache';
import type {
  B2BSaleInvoiceState,
  SalesInvoiceMutationResponse,
  SalesInvoiceSettingsRequest,
  SalesInvoiceSummary,
} from './salesInvoices';

export type B2BSaleStatus = 'draft' | 'confirmed';
export type B2BSaleInvoicing = 'invoice' | 'none';

export interface B2BSaleItemPreview {
  product_id: string;
  quantity: string;
  unit_code: string;
  unit_price: string;
}

export type B2BPaymentState = 'unpaid' | 'partial' | 'paid' | 'unknown' | 'not_required';
export type B2BPaymentUiState = 'awaiting' | 'overdue' | 'paid' | 'unknown' | 'not_required';
export type B2BFulfillmentState = 'unfulfilled' | 'partial' | 'fulfilled';
export type B2BUrgencyFlag = 'overdue' | 'today' | 'soon' | 'neutral';

/** B2B Sale (list item) */
export interface B2BSaleListItem {
  id: string;
  customer_id: string;
  location_id?: string | null;
  /** Warsaw business date (YYYY-MM-DD). */
  sale_date: string;
  /** ISO datetime (TIMESTAMPTZ). */
  sale_at?: string | null;
  total_amount: string;
  status: B2BSaleStatus;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancel_reason?: string | null;
  /** Internal order reference `B2B-YYYYMM-NNNN`. */
  sale_no?: string | null;
  invoicing: B2BSaleInvoicing;
  /** Legacy text number of pre-cutover technical platform rows. */
  legacy_ref?: string | null;
  invoice_state: B2BSaleInvoiceState;
  /** Current non-correction invoice (latest), null when none exists. */
  invoice: SalesInvoiceSummary | null;
  payment_state?: B2BPaymentState;
  payment_ui_state?: B2BPaymentUiState;
  payment_total_amount?: string | null;
  payment_paid_total?: string;
  payment_outstanding_total?: string | null;
  payment_fraction?: string | null;
  payment_active_events_count?: number;
  /** Bank/cash date of the settling money when paid (MD-0092). */
  payment_paid_on?: string | null;
  /** Credit left after corrections (MD-0092). */
  payment_credit_total?: string | null;
  fulfillment_state?: B2BFulfillmentState;
  fulfillment_shipped_lines?: number;
  fulfillment_total_lines?: number;
  fulfillment_shipped_qty?: string;
  fulfillment_ordered_qty?: string;
  fulfillment_fraction?: string | null;
  confirmed_shipments_count?: number;
  /** Earliest confirmed shipment datetime (TIMESTAMPTZ). */
  first_shipped_at?: string | null;
  /** Unique source locations from confirmed shipments. Empty until a confirmed shipment exists. */
  shipment_location_ids?: string[] | null;
  urgency_flag?: B2BUrgencyFlag;
  delivery_address?: string | null;
  notes?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  items_count?: number | null;
  unit_totals?: Record<string, string> | null;
  items_preview?: B2BSaleItemPreview[] | null;
}

/** B2B Sale Item */
export interface B2BSaleItem {
  id: string;
  b2b_sale_id: string;
  line_ref?: string | null;
  product_id: string;
  quantity: string;
  unit_code: string;
  unit_price: string;
  total_price: string;
}

export interface ProformaSellerBankAccount {
  id: string;
  label: string;
  account_number: string;
  bank_name?: string | null;
  account_alias?: string | null;
}

export interface ProformaSeller {
  key: string;
  label: string;
  nip: string;
  seller_name?: string | null;
  address?: string | null;
  legacy_bank_account?: string | null;
  bank_accounts?: ProformaSellerBankAccount[];
}

export interface ProformaSellersResponse {
  request_id: string;
  sellers: ProformaSeller[];
}

/** B2B Sale with items */
export interface B2BSaleDetail extends B2BSaleListItem {
  items: B2BSaleItem[];
  /** All invoices of the order incl. corrections, oldest first. */
  invoices: SalesInvoiceSummary[];
}

export type B2BShipmentStatus = 'draft' | 'confirmed';

export interface B2BShipmentItem {
  id: string;
  b2b_shipment_id: string;
  line_ref: string;
  sale_line_ref: string;
  product_id: string;
  quantity: string;
  unit_code: string;
  unit_price: string;
  notes?: string | null;
}

export interface B2BShipment {
  id: string;
  b2b_sale_id: string;
  location_id: string;
  shipped_at: string;
  status: B2BShipmentStatus;
  notes?: string | null;
  delivery_address?: string | null;
  is_synthetic?: boolean;
  items: B2BShipmentItem[];
  current_revision_no?: number | null;
  current_revision_id?: string | null;
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancel_reason?: string | null;
  /** Sale context (populated in detail/list responses). */
  customer_id?: string | null;
  customer_name?: string | null;
  sale_no?: string | null;
}

export interface B2BSaleDetailResponse {
  request_id: string;
  sale: B2BSaleDetail;
  shipments?: B2BShipment[] | null;
  audit_events?: StockAuditEvent[] | null;
  already_confirmed?: boolean;
  already_cancelled?: boolean;
}

/** B2B Sale filters */
export interface B2BSaleFilters extends PaginationParams {
  [key: string]: string | number | boolean | string[] | undefined;
  q?: string;
  customer_ids?: string[];
  /** Confirmed shipment source locations; falls back to sale location before shipment exists. */
  location_ids?: string[];
  status?: string;
  date_from?: string;
  date_to?: string;
  payment_status?: 'paid' | 'unpaid';
  overdue?: boolean;
  fulfillment_state?: B2BFulfillmentState;
  fulfillment_pending?: boolean;
  sort?: string;
  include_summary?: boolean;
  include_items_preview?: boolean;
}

/** B2B Sales response */
export interface B2BSalesListSummaryByUiStatus {
  all: number;
  draft: number;
  awaiting: number;
  overdue: number;
  paid: number;
}

export interface B2BSalesListSummary {
  by_ui_status: B2BSalesListSummaryByUiStatus;
}

export interface B2BSalesResponse extends ListResponse<B2BSaleListItem> {
  summary?: B2BSalesListSummary | null;
}

/** Create B2B Sale request */
export interface CreateB2BSaleRequest {
  customer_id: string;
  location_id?: string | null;
  sale_date: string;
  invoicing?: B2BSaleInvoicing;
  delivery_address?: string | null;
  notes?: string;
  items: CreateB2BSaleItemRequest[];
  initial_status?: 'draft' | 'confirmed';
}

/** Create B2B Sale item */
export interface CreateB2BSaleItemRequest {
  product_id: string;
  quantity: string;
  unit_code: string;
  unit_price: string;
  total_price: string;
}

export type UpdateB2BSaleDraftRequest = Omit<CreateB2BSaleRequest, 'initial_status'>;

export interface ReviseB2BSaleRequest extends UpdateB2BSaleDraftRequest {
  change_reason?: string;
}

export interface CancelB2BSaleRequest {
  cancel_reason: string;
}

export interface UpdateB2BSaleMetaRequest {
  sale_no?: string;
  notes?: string;
  reason?: string;
}

export interface B2BShipmentItemRequest {
  sale_line_ref?: string;
  product_id?: string;
  quantity: string;
  unit_code?: string;
}

export interface CreateB2BShipmentRequest {
  shipped_at?: string;
  notes?: string;
  initial_status?: B2BShipmentStatus;
  items?: B2BShipmentItemRequest[];
  location_id?: string;
  delivery_address?: string | null;
}

export interface UpdateB2BShipmentDraftRequest {
  shipped_at: string;
  notes?: string;
  items: B2BShipmentItemRequest[];
  location_id?: string;
  delivery_address?: string | null;
}

export interface ConfirmB2BShipmentRequest {
  shipped_at?: string;
}

export interface ReviseB2BShipmentRequest extends UpdateB2BShipmentDraftRequest {
  change_reason?: string;
}

export interface CancelB2BShipmentRequest {
  cancel_reason?: string;
}

export interface B2BShipmentDetailResponse {
  request_id: string;
  shipment: B2BShipment;
  already_confirmed?: boolean;
  already_cancelled?: boolean;
}

export interface DeleteB2BSaleResponse {
  request_id?: string;
  success?: boolean;
  deleted?: boolean;
  message?: string;
}

type B2BSalesStockDocumentEntity = 'b2b_sale' | 'b2b_shipment';

export type StockB2BSalesApiDeps = StockDocumentMutationDeps<B2BSalesStockDocumentEntity>;

function buildB2BSaleDocumentQuery(params: {
  companyKey: string;
  bankAccountId?: string | null | undefined;
  useLegacyBankAccount?: boolean | undefined;
}): string {
  const searchParams = new URLSearchParams();
  searchParams.set('company_key', params.companyKey);
  if (params.bankAccountId) searchParams.set('bank_account_id', params.bankAccountId);
  if (params.useLegacyBankAccount) searchParams.set('use_legacy_bank_account', 'true');
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

function buildB2BSaleProformaPdfUrl(
  saleId: string,
  params: {
    companyKey: string;
    bankAccountId?: string | null | undefined;
    useLegacyBankAccount?: boolean | undefined;
  },
): string {
  return `/api/web-admin/stock/b2b-sales/${saleId}/proforma-pdf${buildB2BSaleDocumentQuery(params)}`;
}

export const createStockB2BSalesApi = ({
  rememberStockDocumentUpdatedAt,
  forgetStockDocumentUpdatedAt,
  withExpectedUpdatedAt,
}: StockB2BSalesApiDeps) => ({
  list: async (filters: B2BSaleFilters = {}): Promise<B2BSalesResponse> => {
    return rememberResponseItemsUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'b2b_sale',
      await api.webAdmin.get<B2BSalesResponse>('/stock/b2b-sales', { params: filters }),
      (response) => response.items,
      (item) => item.id,
      (item) => item.updated_at,
    );
  },

  detail: async (id: string): Promise<B2BSaleDetailResponse> => {
    const response = rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'b2b_sale',
      await api.webAdmin.get<B2BSaleDetailResponse>(`/stock/b2b-sales/${id}`),
      (current) => current.sale.id,
      (current) => current.sale.updated_at,
    );
    return rememberResponseItemsUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'b2b_shipment',
      response,
      (current) => current.shipments ?? [],
      (shipment) => shipment.id,
      (shipment) => shipment.updated_at,
    );
  },

  proformaPdfUrl: (
    saleId: string,
    companyKey: string,
    bankAccountId?: string | null,
    useLegacyBankAccount?: boolean,
  ): string => {
    return buildB2BSaleProformaPdfUrl(saleId, {
      companyKey,
      bankAccountId,
      useLegacyBankAccount,
    });
  },

  proformaSellers: (): Promise<ProformaSellersResponse> =>
    api.webAdmin.get<ProformaSellersResponse>('/stock/b2b-sales/proforma-sellers'),

  create: (
    data: CreateB2BSaleRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<B2BSaleDetailResponse> =>
    api.webAdmin.post<B2BSaleDetailResponse>('/stock/b2b-sales', data, options).then((response) =>
      rememberResponseUpdatedAt(
        rememberStockDocumentUpdatedAt,
        'b2b_sale',
        response,
        (current) => current.sale.id,
        (current) => current.sale.updated_at,
      ),
    ),

  update: (
    id: string,
    data: UpdateB2BSaleDraftRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<B2BSaleDetailResponse> =>
    api.webAdmin
      .put<B2BSaleDetailResponse>(
        `/stock/b2b-sales/${id}`,
        data,
        withExpectedUpdatedAt('b2b_sale', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'b2b_sale',
          response,
          (current) => current.sale.id,
          (current) => current.sale.updated_at,
        ),
      ),

  confirm: (id: string, options?: StockDocumentMutationOptions): Promise<B2BSaleDetailResponse> =>
    api.webAdmin
      .post<B2BSaleDetailResponse>(
        `/stock/b2b-sales/${id}/confirm`,
        undefined,
        withExpectedUpdatedAt('b2b_sale', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'b2b_sale',
          response,
          (current) => current.sale.id,
          (current) => current.sale.updated_at,
        ),
      ),

  /** Create the order's draft invoice, or return the open one (MD-0091). */
  createInvoice: (
    id: string,
    data: SalesInvoiceSettingsRequest = {},
    options?: StockDocumentMutationOptions,
  ): Promise<SalesInvoiceMutationResponse> =>
    api.webAdmin.post<SalesInvoiceMutationResponse>(
      `/stock/b2b-sales/${id}/invoice`,
      data,
      options?.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {},
    ),

  revise: (
    id: string,
    data: ReviseB2BSaleRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<B2BSaleDetailResponse> =>
    api.webAdmin
      .post<B2BSaleDetailResponse>(
        `/stock/b2b-sales/${id}/revise`,
        data,
        withExpectedUpdatedAt('b2b_sale', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'b2b_sale',
          response,
          (current) => current.sale.id,
          (current) => current.sale.updated_at,
        ),
      ),

  cancel: (
    id: string,
    data: CancelB2BSaleRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<B2BSaleDetailResponse> =>
    api.webAdmin
      .post<B2BSaleDetailResponse>(
        `/stock/b2b-sales/${id}/cancel`,
        data,
        withExpectedUpdatedAt('b2b_sale', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'b2b_sale',
          response,
          (current) => current.sale.id,
          (current) => current.sale.updated_at,
        ),
      ),

  deleteDraft: (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<DeleteB2BSaleResponse> =>
    api.webAdmin
      .delete<DeleteB2BSaleResponse>(
        `/stock/b2b-sales/${id}`,
        withExpectedUpdatedAt('b2b_sale', id, options),
      )
      .then((response) => {
        forgetStockDocumentUpdatedAt('b2b_sale', id);
        return response;
      }),

  restore: (id: string, options?: StockDocumentMutationOptions): Promise<B2BSaleDetailResponse> =>
    api.webAdmin
      .post<B2BSaleDetailResponse>(
        `/stock/b2b-sales/${id}/restore`,
        {},
        withExpectedUpdatedAt('b2b_sale', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'b2b_sale',
          response,
          (current) => current.sale.id,
          (current) => current.sale.updated_at,
        ),
      ),

  updateMeta: (
    id: string,
    data: UpdateB2BSaleMetaRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<B2BSaleDetailResponse> =>
    api.webAdmin
      .patch<B2BSaleDetailResponse>(
        `/stock/b2b-sales/${id}/meta`,
        data,
        withExpectedUpdatedAt('b2b_sale', id, options),
      )
      .then((response) =>
        rememberResponseUpdatedAt(
          rememberStockDocumentUpdatedAt,
          'b2b_sale',
          response,
          (current) => current.sale.id,
          (current) => current.sale.updated_at,
        ),
      ),
});
